import React, { useState } from 'react';
import { Grid, List, ListItem, ListItemText, IconButton, FormControlLabel, Checkbox, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { Translate, withLocalize, LocalizeContextProps } from 'react-localize-redux';
import { ButtonCancel, ButtonContinue } from '../mini_components';
import DrugSelector from '../SmartFields/DrugSelector';
import { AllergyType, DrugType } from '../SmartFields/index';
import { makeNoKnownAllergy } from '../../../constants';

/**
 * SHER-287 — "Update allergies" surface.
 *
 * Allergies are a CURRENT STATE clinicians reconcile, not an append-only pile.
 * This surface is PRE-FILLED with the patient's current allergy list and lets the
 * clinician edit the FULL list (add / remove / "no known allergies"); on save it
 * emits the full current list as ONE snapshot. The API persists that as a single
 * snapshot submission and the Clinical Summary projects the latest snapshot
 * (see PatientInvestigation.recomputeClinicalSummary / SHER-287).
 *
 * `UpdateAllergiesView` is presentational: the drug picker is injected via
 * `renderDrugSelector` so it can be exercised in tests / Storybook without the
 * search / offline chain. The default export wires the real DrugSelector.
 *
 * Why NOT reuse the allergy SmartField (SmartFields/index.tsx, mode:"form" +
 * initialState.listElements + removeElement): that component is a 1121-line state
 * machine (BMI special-casing, treatment stop/active filtering, template-medical-
 * history branches, a 500ms mutation-sync guard) that emits a continuous
 * `elementSelected(listElements)` stream rather than a single snapshot on an explicit
 * Save, and has no concept of the mutually-exclusive "no known allergies" toggle this
 * surface needs. Threading those through it would be a brittle coupling, so this stays
 * a focused editor; only the NAMC sentinel record shape is shared (makeNoKnownAllergy).
 */

export interface UpdateAllergiesViewProps {
    country: string;
    /** Current allergies (pre-fill baseline) — drug name + compo-code. */
    currentAllergies: AllergyType[];
    /** Current state is "reviewed, no known allergies". */
    noKnown: boolean;
    onSave: (snapshot: AllergyType[]) => void;
    onCancel: () => void;
    /** Inject the add-allergy control; receives the pick callback. */
    renderDrugSelector: (onPick: (allergy: AllergyType) => void) => React.ReactNode;
    translate: (key: string) => string;
}

/** Dedupe by drug name, first occurrence (with its compo-code) wins. */
function dedupe(list: AllergyType[]): AllergyType[] {
    const seen = new Set<string>();
    const out: AllergyType[] = [];
    for (const a of list) {
        if (!a.allergy || seen.has(a.allergy)) continue;
        seen.add(a.allergy);
        out.push(a);
    }
    return out;
}

export const UpdateAllergiesView: React.FC<UpdateAllergiesViewProps> = ({
    country,
    currentAllergies,
    noKnown,
    onSave,
    onCancel,
    renderDrugSelector,
    translate,
}) => {
    const [list, setList] = useState<AllergyType[]>(dedupe(currentAllergies ?? []));
    const [reviewedNone, setReviewedNone] = useState<boolean>(!!noKnown);

    function addAllergy(allergy: AllergyType) {
        if (!allergy?.allergy) return;
        // Adding a real allergy contradicts "no known" — clear the toggle.
        setReviewedNone(false);
        setList((prev) => dedupe([...prev, allergy]));
    }

    function removeAllergy(name: string) {
        setList((prev) => prev.filter((a) => a.allergy !== name));
    }

    function toggleNoKnown(checked: boolean) {
        // Only reachable with an empty list (SHER-1057): nothing to clear.
        setReviewedNone(checked);
    }

    // An empty list with "no known" unchecked is an ambiguous, un-saveable state: it
    // neither lists an allergy nor asserts the green "no known" state. The API would
    // persist it as ZERO allergy records, which recomputeClinicalSummary can't see
    // (it ranks rows, not submissions), so the prior list would silently stay the
    // latest snapshot and the banner would NOT clear (SHER-287). Force the clinician
    // to either add an allergy or affirm "no known allergies" instead.
    const blockedEmpty = list.length === 0 && !reviewedNone;

    function save() {
        if (blockedEmpty) return; // defensive — the Save button is disabled in this state.
        if (reviewedNone) {
            onSave([makeNoKnownAllergy()]);
            return;
        }
        // `list` is kept deduped on every mutation (init / addAllergy), so no re-dedupe here.
        onSave(list);
    }

    return (
        <Grid container spacing={1} data-testid="update-allergies">
            <Grid item xs={12}>
                <Typography variant="subtitle1">
                    <Translate id="clinicalSummary.allergy.update.title" />
                </Typography>
            </Grid>

            <Grid item xs={12}>
                {list.length === 0 ? (
                    <Typography variant="body2" color="textSecondary" data-testid="update-allergies-empty">
                        <Translate id="clinicalSummary.allergy.update.empty" />
                    </Typography>
                ) : (
                    <List dense data-testid="update-allergies-list">
                        {list.map((a) => (
                            <ListItem
                                key={a.allergy}
                                secondaryAction={
                                    <IconButton
                                        edge="end"
                                        aria-label={translate('general.remove')}
                                        data-testid={`remove-allergy-${a.allergy}`}
                                        onClick={() => removeAllergy(a.allergy)}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                }
                            >
                                <ListItemText primary={a.allergy} />
                            </ListItem>
                        ))}
                    </List>
                )}
            </Grid>

            {/* Add control (real DrugSelector or an injected stub). Hidden once
                "no known allergies" is set — the two states are mutually exclusive. */}
            {!reviewedNone && (
                <Grid item xs={12}>
                    {renderDrugSelector(addAllergy)}
                </Grid>
            )}

            {/* SHER-1057: "documented allergies" and "no known allergies" are mutually
                exclusive, so the toggle is offered only once the list is empty (remove
                every allergy to reach it). This replaces SHER-263 P5's confirmation. */}
            {list.length === 0 && (
            <Grid item xs={12}>
                <FormControlLabel
                    data-testid="update-allergies-noknown-toggle"
                    control={
                        <Checkbox
                            checked={reviewedNone}
                            onChange={(e) => toggleNoKnown(e.target.checked)}
                        />
                    }
                    label={<Translate id="clinicalSummary.allergy.update.noKnownToggle" />}
                />
            </Grid>
            )}

            {blockedEmpty && (
                <Grid item xs={12}>
                    <Typography variant="body2" color="error" data-testid="update-allergies-must-assert">
                        <Translate id="clinicalSummary.allergy.update.mustAssert" />
                    </Typography>
                </Grid>
            )}

            <Grid item xs={12} style={{ paddingTop: '1rem' }}>
                <ButtonCancel onClick={onCancel}><Translate id="general.cancel" /></ButtonCancel>
                &nbsp;
                <ButtonContinue data-testid="update-allergies-save" onClick={save} disabled={blockedEmpty}>
                    <Translate id="general.save" />
                </ButtonContinue>
            </Grid>

        </Grid>
    );
};

interface UpdateAllergiesProps extends LocalizeContextProps {
    country: string;
    currentAllergies: AllergyType[];
    noKnown: boolean;
    onSave: (snapshot: AllergyType[]) => void;
    onCancel: () => void;
}

/** Connected variant: wires the real DrugSelector for live use. */
const UpdateAllergies: React.FC<UpdateAllergiesProps> = (props) => {
    const [error, setError] = useState(false);
    return (
        <UpdateAllergiesView
            country={props.country}
            currentAllergies={props.currentAllergies}
            noKnown={props.noKnown}
            translate={(key) => props.translate(key).toString()}
            onSave={props.onSave}
            onCancel={props.onCancel}
            renderDrugSelector={(onPick) => (
                <DrugSelector
                    type="allergy"
                    variant="outlined"
                    error={error}
                    callbackError={(e) => setError(e)}
                    freeSolo
                    chemicalComponent
                    country={props.country}
                    drugSelected={(drug: DrugType) => onPick({ allergy: drug.name, 'compo-code': drug.id })}
                />
            )}
        />
    );
};

export default withLocalize(UpdateAllergies);
